import { useEffect, useMemo, useState } from 'react';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import DialogStandard from '@bumble/app/components/DialogStandard';
import { Loading } from '@bumble/app/components/Loading';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { IamUserCreate, IamAssignEntity, IamUserEdit } from '@bumble/app/components/iam-user';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import AddLinkIcon from '@mui/icons-material/AddLink';
import EditIcon from '@mui/icons-material/Edit';
import { Card, CardContent } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import { UserAgent } from '../__generated__/data-contracts';
import { IamRole } from '@bumble/app/__generated__/data-contracts';

const columns: GridColDef[] = [
  {
    field: 'username',
    headerName: 'User Name',
    width: 250,
    disableColumnMenu: true,
  },
  {
    field: 'role',
    headerName: 'Role',
    width: 100,
    disableColumnMenu: true,
  },
  {
    field: 'entity_name',
    headerName: 'Entity Name',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
  },
  {
    field: 'name',
    headerName: 'Name',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'mfa_enabled',
    headerName: 'MFA Enabled',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'mfa_type',
    headerName: 'MFA type',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'account_id',
    headerName: 'Account ID',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
  },
  {
    field: 'iam_user_id',
    headerName: 'IAM User ID',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
  },
  {
    field: 'created_at',
    headerName: 'Created at',
    minWidth: 150,
    disableColumnMenu: true,
    valueGetter: (params) => {
      if (params.value === null) return '';
      return DateTime.fromISO(params.value).toLocaleString(DateTime.DATETIME_SHORT);
    },
  },
  //{
  //  field: 'phone',
  //  headerName: 'Phone',
  //  width: 200,
  //  disableColumnMenu: true,
  //},
  //{
  //  field: 'address',
  //  headerName: 'Address',
  //  width: 200,
  //  disableColumnMenu: true,
  //  headerAlign: 'right',
  //  align: 'right',
  //},
];

const columnVisible = {
  account_id: true,
  iam_user_id: true,
  entity_name: true,
  username: true,
  role: true,
  name: true,
  //address: true,
  //phone: true,
  //email: true,
  mfa_enabled: true,
  mfa_type: true,
  created_at: true,
};

export const IamUsers = () => {
  const [showIamCreate, setShowIamCreate] = useState(false);
  const [showAssignEntity, setShowAssignEntity] = useState<boolean>(false);
  const [showIamEdit, setShowIamEdit] = useState<boolean>(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection);
  };
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const selectedRowItem = useMemo(() => {
    return rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : '';
  }, [rowSelectionModel]);
  useEffect(() => {
    if (rowSelectionModel.length === 1) {
      setSelectedItemId(selectedRowItem);
    } else {
      setSelectedItemId('');
    }
  }, [rowSelectionModel, selectedRowItem]);

  const api = useApi();
  const { isLoading, data, refetch } = useQuery(
    ['iamUsers'],
    api.listIamUserApiV1ListIamUserGet.bind(null, {}),
    { enabled: true },
  );

  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);

  const handleDataRefetch = () => {
    refetch();
    toast.success('Refreshed IAM Users.');
  };

  const removeIamUser = useMutation(api.removeIamUserApiV1RemoveIamUserPost, {
    onSuccess() {
      toast.success('IAM User removed.');
      refetch();
    },
    onError: apiError,
  });

  const handleUserDelete = () => {
    removeIamUser.mutate({
      iam_user_id: selectedItemId,
    });
  };

  const handleCreateIamUser = () => {
    setShowIamCreate(true);
  };

  const handleDelete = () => {
    setShowConfirmDelete(true);
  };

  const getEmail = (id: string) => {
    return data?.data.filter((item) => item.iam_user_id === id)[0]?.email
  };

  const getEntityName = (id: string) => {
    return data?.data.filter((item) => item.iam_user_id === id)[0]?.entity_name
  };

  return (
    <PageContent>
      <PageHeader
        pageName="IAM"
        count={data?.data?.length || 0}
      />
      {isLoading ? (
        <Loading />
      ) : (
        <Card elevation={0}>
          <CardContent>
            <DataTableGrid
              rows={
                data?.data.map((item) => {
                  return {
                    ...item,
                    id: item.iam_user_id,
                  };
                }) || []
              }
              columns={columns}
              handleSelectionChange={handleSelectionChange}
              rowSelectionModel={rowSelectionModel}
              itemsSelected={rowSelectionModel.length === 1}
              newLabel="Add IAM User"
              newOnClick={handleCreateIamUser}
              columnVisibilityModel={columnVisible}
              getRowId={(row: UserAgent) => row?.user_agent_id}
              refreshData={handleDataRefetch}
              menuItems={[
                {
                  label: 'Assign Entity',
                  icon: <AddLinkIcon color="info" fontSize="small" />,
                  onClick: () => setShowAssignEntity(true),
                },
                {
                  label: 'Update IAM role',
                  icon: <EditIcon color="info" fontSize="small" />,
                  onClick: () => setShowIamEdit(true),
                },
                {
                  label: 'Delete IAM User',
                  icon: <DeleteForeverOutlinedIcon color="error" fontSize="small" />,
                  onClick: handleDelete,
                  hasDivider: true,
                },
              ]}
            />
          </CardContent>
        </Card>
      )}
      {showIamCreate && (
        <DialogStandard
          open={showIamCreate}
          handleClose={() => setShowIamCreate(false)}
          title="Add IAM User"
        >
          <IamUserCreate
            onSubmit={() => {
              setShowIamCreate(false);
              refetch();
            }}
          />
        </DialogStandard>
      )}
      {showIamEdit && (
        <DialogStandard
          open={showIamEdit}
          handleClose={() => setShowIamEdit(false)}
          title="Edit IAM User"
        >
          <IamUserEdit
            onSubmit={() => {
              setShowIamEdit(false);
              refetch();
            }}
            iamUserId={selectedItemId}
            role={data?.data.find(item => item.iam_user_id === selectedItemId)?.role || IamRole.Readonly}
          />
        </DialogStandard>
      )}
      {showAssignEntity && (
        <DialogStandard
          open={showAssignEntity}
          handleClose={() => setShowAssignEntity(false)}
          title="Assign Entity"
        >
          <IamAssignEntity
            onSubmit={() => {
              setShowAssignEntity(false);
              refetch();
            }}
            iamUserId={selectedItemId}
            entityName={getEntityName(selectedItemId)}
          />
        </DialogStandard>
      )}
      {showConfirmDelete && (
        <DialogConfirm
          open={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          handleConfirm={handleUserDelete}
          title="Delete IAM User?"
          description={`Are you certain you wish to proceed with deleting ${getEmail(selectedItemId)}? Please note that this action is irreversible and will result in the permanent removal of the user agent.`}
          confirmationPhrase={getEmail(selectedItemId) || 'confirm'}
        />
      )}
    </PageContent>
  );
};
